import { Controller } from "stimulus"
import Trix from "trix"

const CONTENT_TYPE = "application/vnd.rubyonrails.blog-table.html"
const MAX_ROWS = 20
const MAX_COLUMNS = 10

export default class extends Controller {
  static targets = ["dialog", "title", "rows", "columns", "header", "grid", "submit"]

  connect() {
    this.selectedRange = null
    this.editingAttachment = null
  }

  get editorElement() {
    return document.querySelector(`trix-editor[toolbar="${this.data.get("toolbarId")}"]`)
  }

  get editor() {
    return this.editorElement && this.editorElement.editor
  }

  keepSelection(event) {
    event.preventDefault()
    if (this.editor) this.selectedRange = this.editor.getSelectedRange().slice()
  }

  open(event) {
    event.preventDefault()
    if (!this.editor) return

    this.selectedRange = this.selectedRange || this.editor.getSelectedRange().slice()
    this.editingAttachment = this.selectedTableAttachment()
    const payload = this.attachmentPayload(this.editingAttachment, this.selectedTableCaption(this.editingAttachment))

    this.rowsTarget.value = payload.rows.length
    this.columnsTarget.value = payload.rows[0].length
    this.headerTarget.checked = payload.header
    this.titleTarget.textContent = this.editingAttachment ? "Modifier le tableau" : "Insérer un tableau"
    this.submitTarget.textContent = this.editingAttachment ? "Mettre à jour le tableau" : "Insérer le tableau"
    this.renderGrid(payload.rows)
    this.dialogTarget.hidden = false

    const firstCell = this.gridTarget.querySelector("input")
    if (firstCell) firstCell.focus()
  }

  close(event) {
    if (event) event.preventDefault()
    this.dialogTarget.hidden = true
    this.editingAttachment = null
    this.selectedRange = null
    if (this.editorElement) this.editorElement.focus()
  }

  resizeGrid() {
    const previousRows = this.gridValues()
    const rowCount = this.clampedValue(this.rowsTarget, MAX_ROWS)
    const columnCount = this.clampedValue(this.columnsTarget, MAX_COLUMNS)
    const rows = Array.from({ length: rowCount }, (_, rowIndex) =>
      Array.from({ length: columnCount }, (_, columnIndex) =>
        (previousRows[rowIndex] && previousRows[rowIndex][columnIndex]) || ""
      )
    )
    this.renderGrid(rows)
  }

  refreshGridLabels() {
    this.gridTarget.querySelectorAll("input").forEach((input) => {
      const row = Number(input.dataset.row)
      const column = Number(input.dataset.column)
      input.placeholder = this.cellLabel(row, column)
      input.setAttribute("aria-label", this.cellLabel(row, column))
    })
  }

  insert(event) {
    event.preventDefault()
    if (!this.editor) return

    const payload = { rows: this.gridValues(), header: this.headerTarget.checked }
    const attachment = new Trix.Attachment({
      content: this.tableHTML(payload),
      contentType: CONTENT_TYPE,
      caption: JSON.stringify(payload),
      previewable: false
    })

    this.editor.recordUndoEntry(this.editingAttachment ? "Modifier le tableau" : "Insérer un tableau")
    if (this.selectedRange) this.editor.setSelectedRange(this.selectedRange)
    this.editor.insertAttachment(attachment)
    this.dialogTarget.hidden = true
    this.editingAttachment = null
    this.selectedRange = null
    this.editorElement.focus()
  }

  selectedTableAttachment() {
    const selectedDocument = this.editor.getSelectedDocument()
    const attachments = selectedDocument && selectedDocument.getAttachments()
    const attachment = attachments && attachments.length === 1 ? attachments[0] : null
    return attachment && attachment.getContentType() === CONTENT_TYPE ? attachment : null
  }

  selectedTableCaption(attachment) {
    if (!attachment) return null

    const selectedDocument = this.editor.getSelectedDocument()
    const attachmentPiece = selectedDocument && selectedDocument.getAttachmentPieceForAttachment(attachment)
    return attachmentPiece && attachmentPiece.getCaption()
  }

  attachmentPayload(attachment, pieceCaption) {
    if (attachment) {
      try {
        // Lors de la première insertion, la donnée se trouve sur l'attachment.
        // Après un enregistrement/rechargement Action Text, Trix la replace
        // dans les attributs de l'AttachmentPiece (caption).
        const serializedPayload = pieceCaption || attachment.getAttribute("caption")
        const payload = JSON.parse(serializedPayload)
        const rowsAreValid =
          Array.isArray(payload.rows) &&
          payload.rows.length > 0 &&
          payload.rows.every((row) => Array.isArray(row)) &&
          payload.rows[0].length > 0
        if (rowsAreValid) {
          const columnCount = Math.min(payload.rows[0].length, MAX_COLUMNS)
          return {
            rows: payload.rows.slice(0, MAX_ROWS).map((row) =>
              Array.from({ length: columnCount }, (_, columnIndex) => String(row[columnIndex] || ""))
            ),
            header: payload.header === true
          }
        }
      } catch (error) {
        // Une pièce jointe mal formée est remplacée par un nouveau tableau.
      }
    }
    return { rows: Array.from({ length: 3 }, () => Array(3).fill("")), header: true }
  }

  renderGrid(rows) {
    this.gridTarget.innerHTML = ""
    this.gridTarget.style.gridTemplateColumns = `repeat(${rows[0].length}, minmax(140px, 1fr))`
    rows.forEach((row, rowIndex) => {
      row.forEach((value, columnIndex) => {
        const input = document.createElement("input")
        input.type = "text"
        input.value = value
        input.dataset.row = rowIndex
        input.dataset.column = columnIndex
        input.placeholder = this.cellLabel(rowIndex, columnIndex)
        input.setAttribute("aria-label", this.cellLabel(rowIndex, columnIndex))
        this.gridTarget.appendChild(input)
      })
    })
  }

  gridValues() {
    const rowCount = this.clampedValue(this.rowsTarget, MAX_ROWS)
    const columnCount = this.clampedValue(this.columnsTarget, MAX_COLUMNS)
    const rows = Array.from({ length: rowCount }, () => Array(columnCount).fill(""))
    this.gridTarget.querySelectorAll("input").forEach((input) => {
      const row = Number(input.dataset.row)
      const column = Number(input.dataset.column)
      if (rows[row] && column < columnCount) rows[row][column] = input.value
    })
    return rows
  }

  clampedValue(input, maximum) {
    const value = Math.max(1, Math.min(maximum, Number(input.value) || 1))
    input.value = value
    return value
  }

  cellLabel(row, column) {
    const prefix = this.headerTarget.checked && row === 0 ? "En-tête" : `Ligne ${row + 1}`
    return `${prefix}, colonne ${column + 1}`
  }

  tableHTML(payload) {
    const rowHTML = (row, cellTag) => `<tr>${row.map((cell) => `<${cellTag}>${this.escapeHTML(cell)}</${cellTag}>`).join("")}</tr>`
    const header = payload.header ? `<thead>${rowHTML(payload.rows[0], "th")}</thead>` : ""
    const bodyRows = payload.header ? payload.rows.slice(1) : payload.rows
    return `<div class="blog-table-wrapper"><table class="blog-table">${header}<tbody>${bodyRows.map((row) => rowHTML(row, "td")).join("")}</tbody></table></div>`
  }

  escapeHTML(value) {
    const element = document.createElement("div")
    element.textContent = value
    return element.innerHTML
  }
}
